import type { Area, Booking, TimeSlot } from "@/store/types";
import { getBookingStatus } from "@/utils/Formatting";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useListStore } from "./listStore";

export const useBooking = defineStore("booking", () => {
  const list = useListStore();
  const userStore = useUserStore();

  const { getListArea } = list;

  const { listArea: areasList } = storeToRefs(list);
  const { currentLocation } = storeToRefs(userStore);

  // data
  const areaLists = ref<any[]>([]);
  const listArea = ref<any[]>([]);
  const editBookingData = ref<any>({});
  const timeRanges = ref<any>([]);
  const activityList = ref<any[]>([]);
  const selectedActivity = ref<any>({});
  const membershipStartHour = ref<number | any>();
  const membershipFinishHour = ref<number | any>();
  const recurringBookingId = ref<any>("");
  const isProcessingCardDialogVisible = ref<boolean>(false);

  const addCardDialogMessage = ref<any>({
    spinner: false,
    isVisible: false,
    message: "",
    color: "",
  });

  // Methods
  const generateTimeSlots = (
    uniqueHourRange: number[],
    spacing: number,
    gap: number,
    selectedDate: any,
    bookings: Array<Booking>,
    recurringBookingsList: any[],
    eventsList: any[],
    recurringEventsList: any[],
    area:any,
    closingTime:number,
    startTime: number,
    memberCanBook:any,
    futureBookingDisabled:boolean,
  ): Array<TimeSlot> => {
    const timeSlots: Array<TimeSlot> = [];
    let parts = uniqueHourRange && uniqueHourRange.length > 0 ? uniqueHourRange[0].toString().split(".") : ["0","0"]
    let minute = 0;
    let inHour = "0"

    if(parts[1] && parseInt(parts[1]) !== 0){
      inHour = parts[1]
      minute = Math.ceil(parseFloat("0." + parts[1]) * 60)
    }

    if(area && area?.offSetMinutes > 0){
      minute += area?.offSetMinutes
    }

    let now: ReturnType<typeof moment>;
    try {
      if (pb.authStore.model?.roles && pb.authStore.model?.roles.length > 0) {
        now = moment().startOf('day').add(1, 'hour').add(area?.offSetMinutes ?? 0, 'minutes');
      } else {
        now = moment().subtract(1, 'hour').startOf('hour');
      }
    } catch(e){
      now = moment();
    }
    const isToday = selectedDate.isSame(now, "day");

    const isYesterday = selectedDate.isSame(
      now.clone().subtract(1, "day"),
      "day",
    );

    const getBooking = (timeForComparison: any) => {
      return bookings?.find((booking) => {
        let bookingStart = moment(booking.startTime);
        let bookingEnd = moment(booking.endTime);

        if(booking?.actualStartTime && booking?.actualStartTime != ''){
          let duration = booking?.actualDuration && booking?.actualDuration > 0 ? booking?.actualDuration : booking.duration
          if(booking.startTime && moment(booking.startTime).isBefore(bookingStart)){
            bookingStart = moment(booking.startTime)
          }
           bookingEnd = moment(booking.startTime).add(duration, "minutes")
        }

        return (
          bookingStart.isSameOrBefore(timeForComparison) &&
          bookingEnd.isAfter(timeForComparison)
        );
      });
    };

    const getRecurringConfig = (booking: Booking) => {
      return recurringBookingsList.find((recurring) =>
        recurring.bookings.includes(booking.id),
      );
    };

    const getEventConfig = (booking: Booking) => {
      return eventsList.find((event) => event.bookings.includes(booking.id));
    };

    const getRecurringEvent = (booking: any) => {
      if (!booking) return;

      return recurringEventsList?.find((list: any) => {
        return list?.expand?.events?.find((event: any) => {
          return event?.expand?.bookings?.find(
            (book: any) => book.id === booking.id,
          );
        });
      });
    };

    // Generate two past slots if today
    if (isToday) {
      let currentTime = moment(selectedDate).set({
        hour: moment().hour(),
        minute: moment().minute(),
        second: 0,
      });

      const firstPastSlot = now
        .clone()
        .startOf("minute")
        .add((spacing - (now.minute() % spacing)), "minutes")
        .subtract(spacing, "minutes"); // Move to one slot before the next slot

      const secondPastSlot = firstPastSlot.clone().subtract(spacing, "minutes");

      [secondPastSlot, firstPastSlot]?.forEach((slot) => {
        slot.add(area?.offSetMinutes ?? 0, "minutes");
        const formattedStartTime = slot.format("HH:mm:ss");

        const timeComparison = moment(selectedDate).set({
          hour: slot.hour(),
          minute: slot.minute(),
          second: 0,
        });

        if (!timeSlots?.find((slot) => slot.startTime === formattedStartTime)) {
          const booking = getBooking(timeComparison);

          const recurringBookingConfig = booking
            ? getRecurringConfig(booking)
            : null;

          const eventConfig = booking ? getEventConfig(booking) : null;
          const recurringEvent = getRecurringEvent(booking);

          let newNow = moment()

          let canMemberBook = false
          let startCheck = moment(selectedDate).add(memberCanBook.startHour,"hours").add(memberCanBook.startMin, "minutes")
          if((startCheck.isSame(slot) && memberCanBook.startHour !== '' && memberCanBook.finishHour !== '')
            || (memberCanBook.startHour === 0 && memberCanBook.finishHour === 24)){
            canMemberBook = true
          }

          let currentHour = parseInt(slot.format("HH"));
          currentHour += parseInt(slot.format("mm")) / 60;

          timeSlots.push({
            startTime: formattedStartTime,
            label: slot.format("h:mm a"),
            canMemberBook,
            futureBookingDisabled,
            isNormalHour: false,
            isAfterHours: currentHour + ((spacing - gap) / 60) > closingTime || currentHour < startTime,
            isPastHour: currentTime.isBefore(newNow),
            ...(booking && {
              isBooked: true,
              bookingDetails: { ...booking, recurring: recurringBookingConfig },
              ...(recurringBookingConfig && { isRecurring: true }),
              ...(eventConfig && {
                isEvent: true,
                event: eventConfig,
                ...(recurringEvent && {
                  isRecurringEvent: true,
                  recurringEventDetails: {
                    id: recurringEvent?.id,
                    days: recurringEvent?.days,
                    endDate: recurringEvent?.endDate,
                    events: recurringEvent?.events,
                    expand: recurringEvent?.expand,
                    frequency: recurringEvent?.frequency,
                    interval: recurringEvent?.interval,
                  },
                }),
              }),
            }),
          });
        }
      });
    }

    // Generate slots for the entire day
    let hourCheck = JSON.parse(JSON.stringify(uniqueHourRange))
    for (let hour = 0; hour < 24; hour++) {
      let currentTime = moment(selectedDate).set({
        hour,
        minute:(area?.offSetMinutes || 0),
        second: 0,
      });

      let newNow = moment()
      // Skip past times if the selected date is today
      if (isToday && currentTime.isBefore(now)) {
        currentTime = now
          .clone()
          .startOf("minute")
          .add(spacing - (now.minute() % spacing), "minutes");
        if (currentTime.hour() !== hour) continue;
      }
      while (currentTime.hour() === hour){
        const formattedStartTime = currentTime.format("HH:mm:ss");

        // Do not generate slots for the next day
        if (currentTime.isAfter(moment(selectedDate).endOf("day"))) break;

        // Create a time for comparison using the selected date and current time
        const timeForComparison = moment(selectedDate).set({
          hour: currentTime.hour(),
          minute: currentTime.minute(),
          second: 0,
        });

        if (!timeSlots?.find((slot) => slot.startTime === formattedStartTime)) {
          const booking = getBooking(timeForComparison);

          const recurringBookingConfig = booking
            ? getRecurringConfig(booking)
            : null;

          const eventConfig = booking ? getEventConfig(booking) : null;
          const recurringEvent = getRecurringEvent(booking);

          if (booking) {
            booking.status = getBookingStatus(
              booking.actualStartTime,
              booking.actualDuration,
            );
          }

          // Does the last spot in hour check go past the closing time?
          if(area && area?.offSetMinutes > 0 && hourCheck.length > 0){
            try {
              let hour = hourCheck[hourCheck.length - 1].toString().split(".")[0]
              if (parseInt(hour) + 1 >= closingTime) {
                hourCheck.splice(hourCheck.length - 1, 1)
              }
            } catch(e){}
          }

          let hourPart = parseFloat(hour.toString() + "." + inHour.toString())
          try{
            let timeParts = hourCheck[0].toString().split(".")
            let hhourCheck = Math.ceil(timeParts[0] || 0)
            let mins = Math.floor(timeParts[1]) || 0
            mins = parseFloat("0." + mins) * 60
            let timeCheck = moment(selectedDate).add(hhourCheck,"hours").add(mins,"minutes");

            if(timeCheck.isAfter(currentTime)){
              hourPart = ''
            }
          } catch(e){}

          const isNormalHour = (hourCheck.includes(hour) || hourCheck.includes(hourPart))

          let isPastHour = currentTime.isBefore(newNow);

          if(isPastHour && spacing){
            let endTime = moment(currentTime).add(spacing,"minutes")
            isPastHour = endTime.isBefore(newNow);
          }

          /** ADD BACK */
          if(booking && booking?.startTime && booking?.startTime != ''){
            let bCheckSt = booking?.startTime
            let bEndTime = moment(bCheckSt).add(booking.duration, "minutes")
            let bTime = new Date(moment(bCheckSt).toISOString()).getTime()
            let slotTime = new Date(currentTime.toISOString()).getTime();
            if(bTime < slotTime && timeSlots[timeSlots.length - 1]){
              // update Time Slot Before
              let timeSlot = {
                startTime: timeSlots[timeSlots.length - 1].startTime,
                label: moment(bCheckSt).format("h:mm a") +
                  ' - ' + moment(bEndTime).format("h:mm a"),
                isNormalHour: timeSlots[timeSlots.length - 1].isNormalHour,
                isPastHour: bEndTime.isBefore(newNow), // timeSlots[timeSlots.length - 1].isPastHour,
                ...(booking && {
                  isBooked: true,
                  bookingDetails: { ...booking, recurring: recurringBookingConfig },
                  ...(recurringBookingConfig && { isRecurring: true }),
                  ...(eventConfig && {
                    isEvent: true,
                    event: eventConfig,
                    ...(recurringEvent && {
                      isRecurringEvent: true,
                      recurringEventDetails: {
                        id: recurringEvent?.id,
                        days: recurringEvent?.days,
                        endDate: recurringEvent?.endDate,
                        events: recurringEvent?.events,
                        expand: recurringEvent?.expand,
                        frequency: recurringEvent?.frequency,
                        interval: recurringEvent?.interval,
                      },
                    }),
                  }),
                }),
              }

              timeSlots[timeSlots.length - 1] = timeSlot
            }
          }
           // */
           /** END ADD BACK */
          let canMemberBook = false
          let startCheck = moment(selectedDate).add(memberCanBook.startHour,"hours").add(memberCanBook.startMin, "minutes")
          let endCheck = moment(selectedDate).add(memberCanBook.finishHour,"hours").add(memberCanBook.finishMin, "minutes").add(spacing * -1, "minutes")
          if((startCheck.isSameOrBefore(currentTime) && currentTime.isSameOrBefore(endCheck) && memberCanBook.startHour !== '' && memberCanBook.finishHour !== '')
          || (memberCanBook.startHour === 0 && memberCanBook.finishHour === 24)){
            canMemberBook = true
          }

          let currentHour = parseInt(currentTime.format("HH"));
          currentHour += parseInt(currentTime.format("mm")) / 60;

          timeSlots.push({
            startTime: formattedStartTime,
            label: currentTime.format("h:mm a"),
            canMemberBook,
            futureBookingDisabled,
            isNormalHour,
            isAfterHours: currentHour + ((spacing - gap) / 60) > closingTime || currentHour < startTime,
            isPastHour,
            ...(booking && {
              isBooked: true,
              bookingDetails: { ...booking, recurring: recurringBookingConfig },
              ...(recurringBookingConfig && { isRecurring: true }),
              ...(eventConfig && {
                isEvent: true,
                event: eventConfig,
                ...(recurringEvent && {
                  isRecurringEvent: true,
                  recurringEventDetails: {
                    id: recurringEvent?.id,
                    days: recurringEvent?.days,
                    endDate: recurringEvent?.endDate,
                    events: recurringEvent?.events,
                    expand: recurringEvent?.expand,
                    frequency: recurringEvent?.frequency,
                    interval: recurringEvent?.interval,
                  },
                }),
              }),
            }),
          });
        }
        currentTime = currentTime.add(spacing, "minutes");
      }
    }

    // For yesterday, trim to only the last two slots
    if (isYesterday && pb.authStore.model?.roles.length == 0) {
      return timeSlots
        .slice(-1) // Get the last two slots
        .sort((a, b) =>
          moment(a.startTime, "HH:mm:ss").diff(moment(b.startTime, "HH:mm:ss")),
        );
    }

    return timeSlots.sort((a, b) =>
      moment(a.startTime, "HH:mm:ss").diff(moment(b.startTime, "HH:mm:ss")),
    );
  };

  /**
   * Retrieves and processes area lists based on the provided activity, location, date, and membership.
   * It fetches areas, bookings, and time ranges, then generates time slots for each area.
   * The function also merges time slots for each area, grouping them by bookingDetails.id.
   *
   * @param activity - The activity object for which to retrieve areas.
   * @param date - The date for which to retrieve areas and bookings.
   * @param membership - The membership for which to adjust time slot availability.
   * @returns An array of areas with their respective time slots.
   */
  const getAreaLists = async (
    activity: any,
    date: any,
    membership: string,
    bookingOverride?: Booking,
  ) => {
    try {
      const dayOfWeek = moment(date, "YYYY-MM-DD")
        .format("dddd")
        ?.toLowerCase();

      // Convert local time boundaries to UTC time
      const startOfDayUTC = moment(date)
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const endOfDayUTC = moment(date)
        .endOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const areas = listArea?.value?.filter((area: Area) =>
        area?.activities?.includes(activity?.id),
      );

      const getBookingList: Booking[] = await pb
        .collection("bookings").getFullList({
          filter: `startTime <= '${endOfDayUTC}' && endTime >= '${startOfDayUTC}' && state != 'Deleted'`,
          expand: "users,docketLine,owner",
          requestKey: null,
        });

      if (bookingOverride?.id) {
        const bookingIndex = getBookingList.findIndex(
          (booking) => booking.id === bookingOverride.id,
        );
        if (bookingIndex >= 0) {
          getBookingList[bookingIndex] = {
            ...getBookingList[bookingIndex],
            ...bookingOverride,
          };
        } else {
          getBookingList.push(bookingOverride);
        }
      }

      // Extract booking IDs from the getBookingList
      const bookingIds = getBookingList.map((booking) => booking.id);

      const [recurringBookingsList, eventsList] = bookingIds.length
        ? await Promise.all([
            pb.collection("recurringBookings").getFullList({
              filter: bookingIds.map((id) => `bookings ~ '${id}'`).join(" || "),
            }),
            pb.collection("events").getFullList({
              filter: bookingIds.map((id) => `bookings ~ '${id}'`).join(" || "),
              expand: "bookings.owner,bookings.docketLine.docket,bookings.area", // Ensure to expand all necessary fields
            }),
          ])
        : [[], []];

      const recurringEventsList = eventsList.length
        ? await pb.collection("recurringEvents").getFullList({
            filter: eventsList
              .map((event) => `events ~ '${event.id}'`)
              .join(" || "),
            expand: "events.bookings.docketLine.docket",
          })
        : [];

      const dateCheck = moment(date)
      const timeRange = timeRanges?.value?.filter(
        (el: any) => {
          if(el?.validFrom && el.validFrom != ''){
            if(dateCheck.isBefore(moment(el.validFrom))){
              return false
            }
          }
          if(el?.validTill && el.validTill != ''){
            if(dateCheck.isAfter(moment(el.validTill))){
              return false
            }
          }
          return el[dayOfWeek] === true && el.allUsers
        }
      );

      let closingTime = 0
      let startTime = 0

      // timeRange is already ordered by sortOrder (via timeRanges.value), so the
      // first matching record for this date/day is the one that applies.
      const matchedTimeRange = timeRange?.[0];

      let hourRange: number[] = [];
      if (matchedTimeRange) {
        let { startHour, finishHour, startMin, finishMin } = matchedTimeRange;
        startHour += startMin == 0 ? 0 : startMin / 60;
        finishHour += finishMin == 0 ? 0 : finishMin / 60;
        closingTime = finishHour;
        startTime = startHour;

        if (finishHour === 0) {
          // Handle wrap-around midnight case
          hourRange = [
            ...Array.from({ length: Math.round(24 - startHour) }, (_, i) => startHour + i), // From startHour to 23
            ...Array.from({ length: finishHour }, (_, i) => i), // From 0 to finishHour (excluding 0)
          ];
        } else {
          hourRange = Array.from(
            { length: Math.round(finishHour - startHour) },
            (_, i) => startHour + i,
          );
        }
      }

      // Remove duplicates, sort, and ensure the range wraps correctly
      let uniqueHourRange:any = []
      try {
        uniqueHourRange = [
          ...new Set(hourRange.map((hour: any) => (hour === 24 ? 0 : hour))),
        ].sort((a, b) => a - b);
      } catch(e){}

      let memberCanBook = {startHour:'',startMin:'',finishHour:'',finishMin:''}
      try {
        const response = await pb.send('/bbapi/times/getAvailableTimes', {
          method: 'POST',
          body: {
            activity: activity?.id,
            membership: membership || pb.authStore?.model?.membership || [],
            date: moment(date).format('YYYY-MM-DD'),
            isAdmin: pb.authStore?.model?.roles?.length > 0,
          },
          requestKey: null,
        });

        // availableTimes is already ordered by sortOrder and filtered to first record user is eligible for 
        const memberTimeRanges = response?.availableTimes?.[0];
        if (memberTimeRanges) {
          memberCanBook.startHour = !isNaN(memberTimeRanges?.startHour) ? (memberTimeRanges.startHour == 0 ? 0 : memberTimeRanges.startHour) : ''
          memberCanBook.startMin =  !isNaN(memberTimeRanges?.startMin) ? (memberTimeRanges.startMin == 0 ? 0 : memberTimeRanges.startMin) : ''
          memberCanBook.finishHour = !isNaN(memberTimeRanges?.finishHour) ? (memberTimeRanges.finishHour == 0 ? 24 : memberTimeRanges.finishHour) : ''
          memberCanBook.finishMin = !isNaN(memberTimeRanges?.finishMin) ? (memberTimeRanges.finishMin == 0 ? 0 : memberTimeRanges.finishMin) : ''
        }
      }catch(e){}

      // assign bookingStatus
      getBookingList?.forEach((booking: any) => {
        booking.status = getBookingStatus(
          booking.actualStartTime,
          booking.actualDuration,
        );
      });

      const updatedAreas = areas?.map((area) => {
        const areaBookings = getBookingList?.filter(
          (booking: any) => booking.area === area.id,
        );

        const timeSlots = generateTimeSlots(
          uniqueHourRange,
          activity?.bookingSpacing,
          activity?.bookingGap,
          moment(date),
          areaBookings,
          recurringBookingsList,
          eventsList,
          recurringEventsList,
          area,
          closingTime,
          startTime,
          memberCanBook,
          activity?.futureBookingDisabled || false
        );

        return { ...area, timeSlots };
      });

      /**
       * Merges time slots for each area, grouping them by bookingDetails.id.
       * Handles time slots without bookingDetails or bookingDetails.id separately.
       *
       * @param updatedAreas - An array of areas with their respective time slots.
       * @returns An array of areas with merged time slots.
       */
      const mergeTimeSlots = updatedAreas?.map((area) => {
        const timeSlotsMap = area?.timeSlots?.reduce(
          (map: any, slot: TimeSlot) => {
            const bookingId = slot?.bookingDetails?.id || slot.label;
            if (!map[bookingId]) {
              map[bookingId] = {
                ...slot,
                slotCount: slot?.bookingDetails?.id ? 1 : undefined,
              };
            } else if (map[bookingId]?.slotCount) {
              map[bookingId].slotCount++;
            }
            return map;
          },
          Object.create(null),
        );

        return { ...area, timeSlots: Object.values(timeSlotsMap) };
      });

      await nextTick()
      areaLists.value = mergeTimeSlots?.filter(area => area) || [];
    } catch (e) {
      console.error(e);
      areaLists.value = [];
    }
  };

  const getActivityList = async (membership = "", date = "") => {
    // Don't trust the user store current location
    let cLocation = localStorage.getItem("homeLocation") ? localStorage.getItem("homeLocation") : currentLocation.value
    try {
      const records = await pb.collection("activities").getFullList({
        expand: "availibleTimes,TODPricing,options, options.category, options.category.TODPricing, options.subCategory, options.subCategory.TODPricing",
        filter: `location = '${cLocation}' && futureBookingDisabled=false`,
        sort:'sortOrder',
        requestKey: null,
      });

      if (records && records.length) {
        activityList.value = records;
        selectedActivity.value = activityList.value[0];
        if(selectedActivity.value) {
          timeRanges.value = (selectedActivity.value?.expand?.availibleTimes || [])
            .slice()
            .sort((a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
          // fetch area with timeSlots
          await getAreaLists(
            selectedActivity.value,
            date || moment().format("YYYY-MM-DD"),
            membership,
          );
        }
      }
    } catch (e) {
      console.error("Error fetching activity list:", e);
      activityList.value = [];
      selectedActivity.value = {};
      timeRanges.value = [];
      areaLists.value = [];
    }
  };

  const getAllAreas = async () => {
    try {
      await getListArea();
      listArea.value = areasList.value;
    } catch (e) {
      console.log("Error in getAllAreas :;", e);
    }
  };

  const setDocketBookingStatus = async (did: string) => {
    try {
      const { expand } = await pb.collection("docketLines")
      .getFirstListItem(`docket = '${did}' && booking!=''`, {
        expand: "booking",
      });

      await pb.collection("bookings").update(expand?.booking?.id, {
        due: 0,
        state: "Confirmed"
      });
    } catch (error) {
      console.error("Error updating docket Record ", error);
    }
  };

  const subscribeBookingPayment = async (
    paymentId: string,
    membership: any = {},
    sessionId: string = "",
  ) => {
    const statusMessages = {
      Canceled: { type: "cancelled", message: "Payment Cancelled" },
      Declined: { type: "declined", message: "Payment Declined" },
      Waiting: { type: "error", message: "Payment Waiting" },
      Pending: { type: "error", message: "Server not responding" },
      Busy: { type: "error", message: "Terminal is Busy" },
      Offline: { type: "offline", message: "Terminal Offline" },
      Approved: { type: "success", message: "Payment Approved" },
      GotWebResponse: { type: "info", message: "Waiting for server response"},
    };

    const pollInterval = 1500;
    let isPolling = true;
    let gotWebResponseHandled = false;
    let pendingStartTime: number | null = null;

    // Ensure polling doesn't proceed if dialog isn't visible
    if (!isProcessingCardDialogVisible.value) return;

    if (paymentId) {
      const pollStatus = async () => {
        try {
          // Stop polling if the dialog is not visible
          if (!isProcessingCardDialogVisible.value) {
            isPolling = false;
            return;
          }

          const record = await pb.collection("docketPayments").getOne(paymentId);

          if (record) {
            const { status, webRedirect, docket } = record;

            if (status in statusMessages) {
              if (status === "Waiting" && !sessionId && webRedirect) {
                localStorage.setItem(
                  "selectedMembership",
                  JSON.stringify(membership),
                );
                window.location.href = webRedirect;
                isPolling = false;

                return;
              }

              if (status === "GotWebResponse" && !gotWebResponseHandled) {
                addCardDialogMessage.value = {
                  isVisible: true,
                  color: "info",
                  message: statusMessages.GotWebResponse.message,
                  spinner: true,
                };
                gotWebResponseHandled = true;

                return;
              }

              if (["Approved", "Canceled"].includes(status)) {
                isPolling = false;

                const { type, message } = statusMessages[status as keyof typeof statusMessages];

                if (status === "Approved") await setDocketBookingStatus(docket);

                addCardDialogMessage.value = {
                  isVisible: true,
                  color: type === "success" ? "success" : "error",
                  message,
                  spinner: false,
                };

                return;
              }

              if (status === "Pending") {
                if (!pendingStartTime) {
                  pendingStartTime = Date.now();
                  addCardDialogMessage.value = {
                    isVisible: true,
                    color: "error",
                    message: "Processing payment...",
                    spinner: true,
                  };
                } else if (Date.now() - pendingStartTime > 3000) {
                  addCardDialogMessage.value = {
                    isVisible: true,
                    color: "error",
                    message: "Server not responding",
                    spinner: false,
                  };
                }

                return;
              }

              if (["Busy", "Declined", "Offline", "Timeout"].includes(status)) {
                addCardDialogMessage.value = {
                  isVisible: true,
                  color: "error",
                  message: `Payment ${status}`,
                  spinner: false,
                };
                isPolling = false;
              }
              return;
            }
          }
        } catch (error) {
          console.error("Error fetching docket payment status:", error);
        } finally {
          if (isPolling) {
            setTimeout(pollStatus, pollInterval);
          }
        }
      };

      // Start polling
      pollStatus();
    }
  };

  const clearBookingStore = () => {
    areaLists.value = [];
    editBookingData.value = {};
    timeRanges.value = [];
    activityList.value = [];
    selectedActivity.value = {};
    membershipFinishHour.value = undefined;
    membershipStartHour.value = undefined;
  };

  return {
    editBookingData,
    addCardDialogMessage,
    isProcessingCardDialogVisible,
    areaLists,
    activityList,
    selectedActivity,
    membershipStartHour,
    membershipFinishHour,
    timeRanges,
    listArea,
    recurringBookingId,
    getAreaLists,
    subscribeBookingPayment,
    getActivityList,
    clearBookingStore,
    getBookingStatus,
    getAllAreas,
  };
});
